import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabaseFunctionsUrl, supabaseAnonKey } from '../config/supabase';
import './Register.css';

const Register = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState('');
  const [linkType, setLinkType] = useState(() => (searchParams.get('type') === 'stakeholder' ? 'stakeholder' : 'client'));
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Invalid registration link.');
      setLoading(false);
      return;
    }
    const check = async () => {
      const url = supabaseFunctionsUrl ? `${supabaseFunctionsUrl}/get-invite` : null;
      if (!url) {
        setError('App not configured.');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey },
          body: JSON.stringify({ link_id: id }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.valid) {
          setValid(true);
          setLinkType(data.link_type === 'stakeholder' ? 'stakeholder' : searchParams.get('type') === 'stakeholder' ? 'stakeholder' : 'client');
          if (data.email) {
            setPrefillEmail(data.email);
            setEmail(data.email);
          }
        } else {
          setError(data.error || 'This link is invalid or has expired.');
        }
      } catch (err) {
        setError(err?.message || 'Failed to check link.');
      }
      setLoading(false);
    };
    check();
  }, [id, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const em = email.trim();
    if (!em) {
      setError('Email is required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    const url = supabaseFunctionsUrl ? `${supabaseFunctionsUrl}/complete-registration` : null;
    if (!url) {
      setError('App not configured.');
      setSubmitting(false);
      return;
    }
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey },
        body: JSON.stringify({ link_id: id, email: em, password, link_type: linkType }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        const message = linkType === 'stakeholder'
          ? 'Account created. Log in with Admin and your email and password to view assigned clients.'
          : 'Account created. Log in with your email and password.';
        navigate('/login', { state: { message }, replace: true });
        return;
      }
      setError(data.error || 'Registration failed.');
    } catch (err) {
      setError(err?.message || 'Registration failed.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="register-page">
        <div className="register-container">
          <p>Checking link…</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="register-page">
        <div className="register-container">
          <h1>Invalid or expired link</h1>
          <p className="register-error">{error}</p>
          <a href="/login" className="register-link">Go to login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <div className="register-container">
        <h1>Create your account</h1>
        <p className="register-intro">
          {linkType === 'stakeholder'
            ? 'Set your password to finish registration. You will log in to the dashboard (use Admin login) and see only the clients an admin assigns to you.'
            : 'Set your password to finish registration. After that you can log in and complete company setup.'}
        </p>
        {error && <p className="register-error">{error}</p>}
        <form onSubmit={handleSubmit} className="register-form">
          <div className="register-field">
            <label htmlFor="register-email">Email *</label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              readOnly={!!prefillEmail}
              autoComplete="email"
            />
          </div>
          <div className="register-field">
            <label htmlFor="register-password">Password *</label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />
          </div>
          <div className="register-field">
            <label htmlFor="register-confirm">Confirm password *</label>
            <input
              id="register-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Same as above"
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="register-submit" disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <a href="/login" className="register-back">Back to login</a>
      </div>
    </div>
  );
};

export default Register;
